import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ColorSwatch } from "@/components/manual-marca/ColorSwatch";
import { IconGrid } from "@/components/manual-marca/IconGrid";
import { ComponentShowcase } from "@/components/manual-marca/ComponentShowcase";
import { AnimationDemo } from "@/components/manual-marca/AnimationDemo";
import { brandColors, typographyScale, spacingScale } from "@/data/brandGuide";
import { toast } from "sonner";
import logoViable from "@/assets/logo-viabiliza.png";
import logoAmbiental from "@/assets/logo-ambiental.png";
import logoSST from "@/assets/logo-sst.png";
import logoArgos from "@/assets/logo-argos.png";

const ManualMarca = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("identidad");

  const exportPDF = () => {
    toast.info("Funcionalidad de exportación PDF en desarrollo");
  };

  const groupedColors = {
    brand: brandColors.filter(c => c.category === "brand"),
    environmental: brandColors.filter(c => c.category === "environmental"),
    system: brandColors.filter(c => c.category === "system"),
    functional: brandColors.filter(c => c.category === "functional"),
    module: brandColors.filter(c => c.category === "module"),
    sidebar: brandColors.filter(c => c.category === "sidebar"),
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-card/95 backdrop-blur-md shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/configuraciones')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Manual de Marca</h1>
                <p className="text-sm text-muted-foreground">Guía completa de identidad visual</p>
              </div>
            </div>
            <Button onClick={exportPDF} variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Exportar PDF
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="grid w-full grid-cols-3 lg:grid-cols-7 h-auto gap-2">
            <TabsTrigger value="identidad" className="text-xs sm:text-sm">
              Identidad
            </TabsTrigger>
            <TabsTrigger value="colores" className="text-xs sm:text-sm">
              Colores
            </TabsTrigger>
            <TabsTrigger value="iconografia" className="text-xs sm:text-sm">
              Iconografía
            </TabsTrigger>
            <TabsTrigger value="tipografia" className="text-xs sm:text-sm">
              Tipografía
            </TabsTrigger>
            <TabsTrigger value="componentes" className="text-xs sm:text-sm">
              Componentes
            </TabsTrigger>
            <TabsTrigger value="espaciado" className="text-xs sm:text-sm">
              Espaciado
            </TabsTrigger>
            <TabsTrigger value="animaciones" className="text-xs sm:text-sm">
              Animaciones
            </TabsTrigger>
          </TabsList>

          {/* Identidad Visual */}
          <TabsContent value="identidad" className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle>Identidad Visual</CardTitle>
                <CardDescription>
                  Logos, variantes y directrices de uso de la marca Viable
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* Logo Principal */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Logo Principal</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="p-8 bg-white rounded-lg border">
                      <img src={logoViable} alt="Logo Viabiliza" className="w-full max-w-xs mx-auto" />
                      <p className="text-sm text-center mt-4 text-muted-foreground">
                        Fondo claro
                      </p>
                    </div>
                    <div className="p-8 bg-slate-900 rounded-lg border">
                      <img src={logoViable} alt="Logo Viabiliza" className="w-full max-w-xs mx-auto" />
                      <p className="text-sm text-center mt-4 text-white">
                        Fondo oscuro
                      </p>
                    </div>
                  </div>
                </div>

                {/* Logos de Módulos */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Logos de Módulos</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    <div className="p-6 bg-muted rounded-lg text-center">
                      <img src={logoAmbiental} alt="Logo Ambiental" className="w-32 h-32 mx-auto object-contain" />
                      <p className="text-sm font-medium mt-3">Módulo Ambiental</p>
                    </div>
                    <div className="p-6 bg-muted rounded-lg text-center">
                      <img src={logoSST} alt="Logo SST" className="w-32 h-32 mx-auto object-contain" />
                      <p className="text-sm font-medium mt-3">Módulo SST</p>
                    </div>
                    <div className="p-6 bg-muted rounded-lg text-center">
                      <img src={logoArgos} alt="Logo Argos" className="w-32 h-32 mx-auto object-contain" />
                      <p className="text-sm font-medium mt-3">Logo Argos</p>
                    </div>
                  </div>
                </div>

                {/* Directrices de Uso */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Directrices de Uso</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base text-viable-green">✓ Uso Correcto</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-2">
                        <p>• Mantener proporciones originales</p>
                        <p>• Usar sobre fondos de alto contraste</p>
                        <p>• Respetar área de protección mínima</p>
                        <p>• Tamaño mínimo: 120px de ancho</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base text-destructive">✗ Uso Incorrecto</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-2">
                        <p>• No distorsionar proporciones</p>
                        <p>• No cambiar colores del logo</p>
                        <p>• No rotar el logo</p>
                        <p>• No aplicar efectos o sombras</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Colores */}
          <TabsContent value="colores" className="space-y-8">
            {Object.entries(groupedColors).map(([category, colors]) => (
              <div key={category} className="space-y-4">
                <h3 className="text-lg font-semibold capitalize">
                  {category === "brand" && "Colores de Marca"}
                  {category === "environmental" && "Colores Ambientales"}
                  {category === "system" && "Colores del Sistema"}
                  {category === "functional" && "Colores Funcionales"}
                  {category === "module" && "Colores de Módulos"}
                  {category === "sidebar" && "Colores de Sidebar"}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {colors.map((color) => (
                    <ColorSwatch key={color.variable} {...color} />
                  ))}
                </div>
              </div>
            ))}
          </TabsContent>

          {/* Iconografía */}
          <TabsContent value="iconografia" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Iconografía</CardTitle>
                <CardDescription>
                  Sistema de iconos de Lucide React. Haz clic en cualquier icono para copiar el código de importación.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <IconGrid />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tipografía */}
          <TabsContent value="tipografia" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Escala Tipográfica</CardTitle>
                <CardDescription>
                  Jerarquía de texto y tamaños estándar del sistema
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {typographyScale.map((typo) => (
                  <div key={typo.name} className="border-b pb-4 last:border-0">
                    <p className={typo.class}>{typo.name.toUpperCase()}</p>
                    <div className="mt-2 flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <code className="bg-muted px-2 py-1 rounded">{typo.class}</code>
                      <span>{typo.size}</span>
                      <span className="text-xs">{typo.usage}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Pesos de Fuente</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="font-normal">Normal - font-normal (400)</p>
                <p className="font-medium">Medium - font-medium (500)</p>
                <p className="font-semibold">Semibold - font-semibold (600)</p>
                <p className="font-bold">Bold - font-bold (700)</p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Componentes */}
          <TabsContent value="componentes">
            <Card>
              <CardHeader>
                <CardTitle>Componentes UI</CardTitle>
                <CardDescription>
                  Biblioteca de componentes con sus variantes y estados
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ComponentShowcase />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Espaciado */}
          <TabsContent value="espaciado">
            <Card>
              <CardHeader>
                <CardTitle>Sistema de Espaciado</CardTitle>
                <CardDescription>
                  Escala de espaciado basada en Tailwind CSS
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {spacingScale.map((space) => (
                    <div key={space.value} className="flex items-center gap-6 border-b pb-4">
                      <div className="w-32">
                        <code className="text-sm bg-muted px-2 py-1 rounded">
                          {space.value}
                        </code>
                      </div>
                      <div className="w-24 text-sm text-muted-foreground">
                        {space.px}
                      </div>
                      <div
                        className="h-8 bg-primary"
                        style={{ width: space.px }}
                      />
                      <div className="flex-1 text-sm text-muted-foreground">
                        {space.usage}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Animaciones */}
          <TabsContent value="animaciones">
            <Card>
              <CardHeader>
                <CardTitle>Animaciones y Transiciones</CardTitle>
                <CardDescription>
                  Sistema de animaciones para interacciones fluidas
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AnimationDemo />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ManualMarca;
